import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import { sendAnalyticsEvents } from '../analytics';
import type { DreamDriftHero } from './dreamDrift.ruleset';
import { dreamDriftGetInstanceRemainingTime } from './dreamDrift.getters';
import {
  getDreamDriftCompleteAnalyticsEvent,
  getDreamDriftHeroSwitchAnalyticsEvent,
  getDreamDriftStartAnalyticsEvent,
} from './dreamDrift.analytics';
import {
  dreamDriftAcknowledgeMapTransition,
  dreamDriftClaimRewards,
  type DreamDriftLevelFinishResult,
  dreamDriftFinishLevel,
  dreamDriftResolveInstance,
  dreamDriftSetSeenAt,
  dreamDriftStartInstance,
  dreamDriftStartLevel,
  dreamDriftSwitchHero,
} from './dreamDrift.modifiers';

/** Replicant actions supporting the initial Dream Drift backend lifecycle. */
export const dreamDriftActions = createActions({
  /** Record when the player sees an available Dream Drift event. */
  dreamDriftSeenAt: action((state, _args: void, api) => {
    // Reconcile terminal or timed-out instances without rolling back active play.
    const now = api.date.now();
    const instance = state.dreamDrift?.instance;
    const instanceNeedsReconciliation =
      !!instance &&
      (instance.result !== undefined ||
        dreamDriftGetInstanceRemainingTime(state, now) === 0);
    if (instanceNeedsReconciliation) {
      dreamDriftResolveInstance(state, now, api);
    }

    // Record FTUE state using the same server-authoritative time.
    dreamDriftSetSeenAt(state, now);
  }),

  /** Claim rewards available from the retained Dream Drift instance. */
  dreamDriftClaimRewards: action((state, _args: void, api) => {
    // Resolve any elapsed instance before validating its reward availability.
    const now = api.date.now();
    dreamDriftResolveInstance(state, now, api);

    // Grant the combined completion reward or expired progress milestone.
    return dreamDriftClaimRewards(state, now, api);
  }),

  /** Acknowledge a visual transition after its map presentation completes. */
  dreamDriftAcknowledgeMapTransition: action(
    (
      state,
      args: {
        startedAt: number;
        pendingProgress: number;
        progress: number;
      },
      api,
    ) => {
      // Validate and acknowledge only the transition the client presented.
      return dreamDriftAcknowledgeMapTransition(state, {
        ...args,
        now: api.date.now(),
      });
    },
  ),

  /** Start a playable instance after the player confirms an initial hero. */
  dreamDriftStartInstance: action(
    (state, args: { hero: DreamDriftHero }, api) => {
      // Create the instance using one server-authoritative timestamp.
      const startedAt = dreamDriftStartInstance(state, {
        hero: args.hero,
        now: api.date.now(),
      });
      if (startedAt === undefined) return undefined;

      // Emit the lifecycle start only after the backend accepts the instance.
      const startEvent = getDreamDriftStartAnalyticsEvent(state);
      if (startEvent) sendAnalyticsEvents(api, [startEvent]);

      // Return the exact timestamp used as the instance identifier.
      return startedAt;
    },
  ),

  /** Select the hero immediately before the next Dream Drift level. */
  dreamDriftSwitchHero: action(
    (state, args: { startedAt: number; hero: DreamDriftHero }, api) => {
      // Retain the previous value before the modifier mutates the instance.
      const previousHero = state.dreamDrift?.instance?.hero;

      // Apply the pre-level selection to the matching ongoing instance.
      const switched = dreamDriftSwitchHero(state, {
        ...args,
        now: api.date.now(),
      });
      if (!switched) return false;

      // Emit only accepted transitions; unchanged selections remain successful.
      const switchEvent = getDreamDriftHeroSwitchAnalyticsEvent(
        state,
        previousHero,
      );
      if (switchEvent) sendAnalyticsEvents(api, [switchEvent]);

      // Return the accepted switch result.
      return true;
    },
  ),

  /** Mark the next expected Dream Drift node as actively playing. */
  dreamDriftStartLevel: action(
    (state, args: { startedAt: number; node: number }, api) => {
      // Start the matching node using server-authoritative time.
      return dreamDriftStartLevel(state, {
        ...args,
        now: api.date.now(),
      });
    },
  ),

  /** Finish the active Dream Drift node without settling its pending rewards. */
  dreamDriftFinishLevel: action(
    (
      state,
      args: {
        startedAt: number;
        result: DreamDriftLevelFinishResult['result'];
      },
      api,
    ) => {
      // Validate and apply the authoritative level finish first.
      const finishResult = dreamDriftFinishLevel(state, args);
      if (!finishResult) return undefined;

      // Read progression and terminal state from the updated instance.
      const instance = state.dreamDrift?.instance;
      if (!instance) return undefined;

      // Completion is emitted once by the same idempotent final finish action.
      if (instance.result === 'completed') {
        const completionEvent = getDreamDriftCompleteAnalyticsEvent(state);
        if (completionEvent) sendAnalyticsEvents(api, [completionEvent]);
      }

      // Return the accepted level result; map opening owns reward settlement.
      return finishResult;
    },
  ),
});
